import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
type FAQItem = {
  question: string;
  answer: string;
};
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs: FAQItem[] = [{
    question: '¿Qué aprenderé exactamente en estos ebooks?',
    answer: 'Aprenderás desde la selección de materiales (vidrios, espejos, madera) hasta la instalación eléctrica de LEDs y el ensamblaje final para lograr el efecto de profundidad perfecta.'
  }, {
    question: '¿Necesito herramientas profesionales de carpintería?',
    answer: 'No necesariamente. Los manuales incluyen alternativas tanto para quienes tienen un taller completo como para quienes desean armar su proyecto con herramientas básicas de mano.'
  }, {
    question: '¿El contenido incluye la parte electrónica?',
    answer: 'Sí. Se detalla cómo elegir la fuente de alimentación correcta, cómo conectar las tiras LED y cómo configurar controladores para efectos de color.'
  }, {
    question: '¿Cómo recibo mi ebook tras la compra?',
    answer: 'El envío es automático e inmediato. Una vez confirmado el pago, recibirás un enlace de descarga en tu correo electrónico en formato PDF.'
  }, {
    question: '¿Puedo leer los manuales desde mi celular?',
    answer: 'Totalmente. Los archivos están optimizados para visualizarse correctamente en smartphones, tablets y computadoras.'
  }, {
    question: '¿Qué pasa si tengo una duda técnica mientras construyo?',
    answer: 'Contamos con un grupo exclusivo o canal de soporte (según el paquete que elijas) donde respondemos consultas puntuales sobre los proyectos del ebook.'
  }, {
    question: '¿Los materiales que mencionan son fáciles de conseguir?',
    answer: 'Sí, nos enfocamos en materiales que puedes encontrar en cualquier ferretería, vidriería o tienda de electrónica local.'
  }, {
    question: '¿Son proyectos aptos para principiantes?',
    answer: 'Definitivamente. Los ebooks están redactados paso a paso, con diagramas claros y explicaciones sencillas sin tecnicismos innecesarios.'
  }, {
    question: '¿Ofrecen garantía de satisfacción?',
    answer: 'Si el archivo presenta fallas de lectura o no corresponde a lo ofrecido, te brindamos soporte técnico inmediato para solucionar el inconveniente.'
  }, {
    question: '¿Los ebooks incluyen planos con medidas?',
    answer: 'Sí, incluimos planos guía con medidas estándar que puedes adaptar según el tamaño de mesa o espejo que desees fabricar.'
  }, {
    question: '¿Es una estafa? ¿Cómo sé que recibiré el material?',
    answer: 'Entendemos tu duda. No es una estafa; somos una empresa real con miles de alumnos. El proceso de pago es gestionado por plataformas seguras como Mercado Pago, y el envío del material es automático e inmediato a tu email tras la compra. Además, contás con 7 días de garantía.'
  }];
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="py-12 px-4 bg-gray-50 relative z-10 border-t border-gray-200" data-id="element-100">
      <div className="max-w-3xl mx-auto" data-id="element-101">
        <div className="text-center mb-8" data-id="element-102">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-gray-300 text-gray-600 text-sm font-medium mb-3" data-id="element-103">
            <span className="flex h-2 w-2 rounded-full bg-[#00f0ff]" data-id="element-104"></span>
            ¿Tenés dudas?
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-2 tracking-tight" data-id="element-105">
            Preguntas Frecuentes
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-gray-600" data-id="element-106">
            Todo lo que necesitás saber antes de empezar
          </motion.p>
        </div>

        <div className="space-y-4" data-id="element-107">
          {faqs.map((faq, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.05
        }} className="bg-white border border-gray-300 rounded-2xl overflow-hidden hover:border-gray-400 transition-colors shadow-sm" data-id="element-108">
              <button onClick={() => toggleFAQ(index)} aria-expanded={openIndex === index} className="w-full flex items-center justify-between p-4 text-left focus:outline-none group" data-id="element-109">
                <span className="text-lg font-bold text-gray-900 pr-8 group-hover:text-blue-600 transition-colors" data-id="element-110">
                  {faq.question}
                </span>
                <motion.div animate={{
              rotate: openIndex === index ? 180 : 0
            }} transition={{
              duration: 0.3
            }} className="shrink-0" data-id="element-111">
                  <ChevronDownIcon className="w-5 h-5 text-gray-500 group-hover:text-blue-600 transition-colors" data-id="element-112" />
                </motion.div>
              </button>

              <AnimatePresence data-id="element-113">
                {openIndex === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="overflow-hidden" data-id="element-114">
                    <div className="px-4 pb-4 text-gray-700 leading-relaxed border-t border-gray-200 pt-3" data-id="element-115">
                      {faq.answer}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>

        {/* Support CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3
      }} className="mt-12 p-6 bg-white border border-gray-300 rounded-2xl text-center shadow-sm" data-id="element-116">
          <p className="text-gray-600 mb-3" data-id="element-117">
            ¿Todavía tenés dudas o problemas con tu compra?
          </p>
          <a href="mailto:soportesinfinstudio@gmail.com" className="inline-block bg-gray-900 text-white font-bold px-6 py-3 rounded-xl hover:bg-gray-800 transition-colors" data-id="element-118">
            Contactanos: soportesinfinstudio@gmail.com
          </a>
        </motion.div>
      </div>
    </section>;
}